
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import VIEWER from '@generio-ai/library-legacy/constants/viewer'
import { requires } from '@/utils/validate.js'


export default {

    // await readiness of asset files
    async awaitReady({ dispatch }, {
        assets = null,
        fileKey = ASSETS.FILES.KEY.DEFAULT,
        delayBase = 500, // in milliseconds (0.5 secs)
        delayIncrement = 0, // in milliseconds (0.0 secs)
        waitMaximum = 60_000 * 15, // in milliseconds (15 mins)
    }) {
        // ensure parameter is present
        requires(assets, fileKey)

        return Promise.all(
            assets.map(async (asset) => {

                // wait for this asset to be ready
                const response = await dispatch("assets/fetchFileStatus", {
                    asset,
                    fileKey,
                    delayBase,
                    delayIncrement,
                    waitMaximum
                }, { root: true })

                // ensure status is ready
                if (response.status !== ASSETS.FILES.STATUS.READY) {
                    throw new Error("Awaiting asset failed.")
                }
            })
        )
    },

    // await retrieval of asset files 
    async awaitRetrieved({ dispatch }, {
        assets = null,
        fileKey = ASSETS.FILES.KEY.DEFAULT,
        delayBase = 500, // in milliseconds (0.5 secs)
        delayIncrement = 0, // in milliseconds (0.0 secs)
        waitMaximum = 60_000 * 15, // in milliseconds (15 mins)
        onPending = null,
        onReady = null,
        onError = null
    }) {
        // ensure parameter is present
        requires(assets)

        return Promise.all(
            assets.map(asset =>
                dispatch("assets/fetchFile", {
                    asset,
                    fileKey,
                    delayBase,
                    delayIncrement,
                    waitMaximum,
                    onPending,
                    onReady,
                    onError
                }, { root: true })
            )
        )
    },

    // await retrieved model 
    async awaitRetrievedModel({ dispatch }, {
        modelAsset = null,
        onProgress = null,
        onResolve = null
    }) {
        // ensure parameter is present
        requires(modelAsset)

        // list of steps for generation
        const steps = {
            "model structure": {
                finished: false,
                stage: VIEWER.STAGE.STRUCTURE
            },
            "model geometry": {
                finished: false,
                stage: VIEWER.STAGE.GEOMETRY
            },
            "model mapping": {
                finished: false,
                stage: VIEWER.STAGE.MAPPING
            },
            "projected model texture": {
                finished: false,
                stage: VIEWER.STAGE.TEXTURE
            },
            "native model texture": {
                finished: false,
                stage: VIEWER.STAGE.TEXTURE
            },
            "hybrid model texture": {
                finished: false,
                stage: VIEWER.STAGE.TEXTURE
            }
        }

        // track progress
        var lastProgress = null
        var lastStep = null
        var lastStage = VIEWER.STAGE.ANALYZE

        // wait for generation to be finished
        await dispatch("awaitRetrieved", {
            assets: [modelAsset],
            delayBase: 2000,
            onPending: async (response) => {
                // get progress
                var currentProgress = response.details
                // handle progress update
                if (currentProgress !== lastProgress) 
                {
                    // get the step order as keys
                    const stepOrder = Object.keys(steps)

                    // find the current step based on the progress string
                    const currentStep = stepOrder.find(step =>
                        currentProgress.includes(step)
                    )

                    // update last step if current step is found
                    if (currentStep) {

                        // get the index of the current stage
                        const currentIndex = stepOrder.indexOf(currentStep)

                        // last step is the one before the current one
                        if (currentIndex > 0) {
                            lastStep = stepOrder[currentIndex - 1]
                            lastStage = steps[lastStep].stage
                        }

                        // finish the last step (if it did not happen so far)
                        if (lastStep && !steps[lastStep].finished) {

                            // mark the last step as finished
                            steps[lastStep].finished = true

                            // fetch the correct part of the asset
                            if (lastStage === VIEWER.STAGE.STRUCTURE) {
                                await dispatch("assets/fetchModelBase", {
                                    asset: modelAsset,
                                    ignoreReady: true
                                }, { root: true })
                            } else {
                                await dispatch("assets/fetchFile", {
                                    asset: modelAsset,
                                    ignoreReady: true
                                }, { root: true })
                            }
                        }
                    }

                    // inform user about progress
                    if (onProgress) {
                        onProgress(currentProgress, lastStage, [modelAsset])
                    }
                }
            },
            onError: async () => {
                throw new Error("Failed to retrieve model")
            }
        })

        // ensure the model asset is fetched
        await dispatch("assets/fetchFile", {
            asset: modelAsset,
        }, { root: true })

        // select the correct final stage
        if ([VIEWER.STAGE.ANALYZE, VIEWER.STAGE.NOISE, VIEWER.STAGE.STRUCTURE].includes(lastStage)) {
            lastStage = VIEWER.STAGE.GEOMETRY
        } else {
            lastStage = VIEWER.STAGE.TEXTURE
        }

        // resolved successfully
        if (onResolve) {
            onResolve({
                assets: [modelAsset],
                stage: lastStage
            })
        }
    }
}
